import { useEffect, useMemo, useState } from "react";
import { SlidersHorizontal, LockSimple, FloppyDisk, CheckCircle } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useDialog } from "../components/Dialog";
import { ALL_TOGGLE_ROLES } from "../lib/features";

const ROLE_LABELS = { OWNER: "Owner", TENANT: "Tenant", MANAGER: "Manager", SECURITY: "Security", CHAIRPERSON: "Chairman" };

// Admin-only: enable/disable each feature module and choose which roles see it.
// Mirrors MyGate's module admin. Writes only the diff vs. server defaults.
export default function FeatureModules() {
  const { notify } = useDialog();
  const [modules, setModules] = useState(null); // {key:{label,category,enabled,roles,locked}}
  const [order, setOrder] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  async function load() {
    const { data } = await api.get("/api/config/features");
    setModules(data.modules);
    setOrder(Object.keys(data.modules));
    setDirty(false);
  }
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    if (!modules) return [];
    const by = {};
    for (const key of order) {
      const m = modules[key];
      (by[m.category] = by[m.category] || []).push(key);
    }
    return Object.entries(by);
  }, [modules, order]);

  function toggleEnabled(key) {
    setModules((prev) => ({ ...prev, [key]: { ...prev[key], enabled: !prev[key].enabled } }));
    setDirty(true);
  }
  function toggleRole(key, role) {
    setModules((prev) => {
      const roles = new Set(prev[key].roles);
      roles.has(role) ? roles.delete(role) : roles.add(role);
      return { ...prev, [key]: { ...prev[key], roles: ALL_TOGGLE_ROLES.filter((r) => roles.has(r)) } };
    });
    setDirty(true);
  }

  async function save() {
    setSaving(true);
    // Send only editable (non-locked) modules as overrides.
    const overrides = {};
    for (const key of order) {
      const m = modules[key];
      if (m.locked) continue;
      overrides[key] = { enabled: m.enabled, roles: m.roles };
    }
    try {
      await api.put("/api/config/features", { overrides });
      await load();
      notify({ title: "Saved", message: "Module settings updated. Apps refresh on next open." });
    } catch {
      notify({ title: "Save failed", message: "Could not save module settings. Try again." });
    } finally {
      setSaving(false);
    }
  }

  if (!modules) return <div className="text-white/60 text-sm">Loading…</div>;

  return (
    <div className="space-y-4 font-sans" data-testid="feature-modules-screen">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.32em] text-white/50">System</p>
          <h1 className="font-display text-3xl font-extrabold text-white">Feature Modules</h1>
        </div>
        <button onClick={save} disabled={!dirty || saving}
          className="inline-flex items-center gap-2 rounded-2xl bg-neon-cyan px-4 py-2.5 text-[13px] font-bold text-black shadow-glow-cyan disabled:opacity-40">
          <FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="card flex items-start gap-3 p-4">
        <SlidersHorizontal size={20} weight="duotone" className="mt-0.5 shrink-0 text-neon-cyan" />
        <p className="text-[12px] text-white/65">Turn a module on/off and pick which roles can use it. A disabled module disappears from every app (web, Android, iOS) and its API is blocked. Changes apply when a user next opens the app.</p>
      </div>

      {grouped.map(([category, keys]) => (
        <div key={category} className="space-y-2">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/45">{category}</p>
          {keys.map((key) => {
            const m = modules[key];
            return (
              <div key={key} className="card p-4" data-testid={`fm-${key}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="flex items-center gap-1.5 font-display text-[15px] font-semibold text-white">
                      {m.label}
                      {m.locked && <LockSimple size={13} weight="bold" className="text-white/40" />}
                    </p>
                    <p className="text-[11px] text-white/45">{key}</p>
                  </div>
                  {m.locked ? (
                    <span className="rounded-full bg-white/5 px-3 py-1.5 text-[11px] font-bold text-white/50">Always on</span>
                  ) : (
                    <button onClick={() => toggleEnabled(key)} data-testid={`fm-toggle-${key}`}
                      className={`relative h-7 w-12 shrink-0 rounded-full transition ${m.enabled ? "bg-neon-lime" : "bg-white/15"}`}>
                      <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white transition-all ${m.enabled ? "left-[22px]" : "left-0.5"}`} />
                    </button>
                  )}
                </div>

                {!m.locked && (
                  <div className={`mt-3 flex flex-wrap gap-2 ${m.enabled ? "" : "pointer-events-none opacity-40"}`}>
                    {ALL_TOGGLE_ROLES.map((r) => {
                      const on = m.roles.includes(r);
                      return (
                        <button key={r} onClick={() => toggleRole(key, r)} data-testid={`fm-${key}-${r}`}
                          className={`inline-flex items-center gap-1 rounded-xl border px-3 py-1.5 text-[12px] font-semibold ${on ? "border-neon-cyan/60 bg-neon-cyan/10 text-neon-cyan" : "border-white/10 bg-white/[0.03] text-white/55"}`}>
                          {on && <CheckCircle size={13} weight="fill" />} {ROLE_LABELS[r]}
                        </button>
                      );
                    })}
                    <span className="ml-auto self-center text-[10.5px] text-white/35">Admin always has access</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
